import { Container, Dialog, DialogContent, DialogTitle } from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast as toastify } from "react-toastify";
import axios from "axios";
import { server } from "../server";
import {
  MdCategory,
  MdDelete,
  MdAdd,
  MdEdit,
  MdFilterList,
  MdSearch,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import Loading from "../Components/global/Loading";
import { useDispatch, useSelector } from "react-redux";
import {
  getAllCategoriesList,
  getAllSubCategoriesList,
} from "../redux/actions/contents";
import { IoArrowBack, IoClose } from "react-icons/io5";
import { motion } from "framer-motion";
import PageName from "../Components/global/PageName";
import { GiSparkles } from "react-icons/gi";
import { BiGitBranch } from "react-icons/bi";

export default function SubCategoryManager() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    categoriesList,
    subCategoriesList,
    loading: reduxLoading,
  } = useSelector((state) => state?.content);

  const [subCategory, setSubCategory] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [filterCategoryId, setFilterCategoryId] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  // Edit Modal State
  const [editItem, setEditItem] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getAllCategoriesList(1, 100));
  }, [dispatch]);

  useEffect(() => {
    const catId = filterCategoryId === "all" ? null : filterCategoryId;
    dispatch(getAllSubCategoriesList(1, 100, searchQuery, catId));
  }, [sync, filterCategoryId, searchQuery, dispatch]);

  const handleAddSubCategory = async (e) => {
    if (e) e.preventDefault();

    if (!selectedCategoryId) {
      toastify.warning("Please select a parent category.");
      return;
    }

    if (!subCategory || subCategory.trim().length === 0) {
      toastify.warning("Please enter a subcategory name.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(`${server}/subcategory/new`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          subCategory: subCategory.trim(),
          categoryId: selectedCategoryId,
        },
      });

      toastify.success(data?.message || "SubCategory added successfully.");
      setSubCategory("");
      setSync((prev) => prev + 1);
    } catch (err) {
      toastify.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSubCategory = async (item) => {
    const userRes = window.confirm(
      `Are you sure you want to delete the subcategory "${item?.subCategory}"?`,
    );
    if (!userRes) return;

    try {
      setLoading(true);
      const { data } = await axios.delete(`${server}/subcategory/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          subCategoryId: item?.id,
        },
      });
      toastify.success(data?.message || "SubCategory deleted.");
      setSync((prev) => prev + 1);
    } catch (err) {
      toastify.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (item) => {
    setEditItem(item);
    setEditName(item?.subCategory || "");
    setEditCategoryId(item?.categoryId || "");
    setEditModalOpen(true);
  };

  const handleUpdateSubCategory = async (e) => {
    if (e) e.preventDefault();
    if (!editName || editName.trim().length === 0) {
      toastify.warning("Subcategory name cannot be empty.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.put(
        `${server}/subcategory/updateById`,
        null,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: {
            subCategoryId: editItem?.id,
            subCategory: editName.trim(),
            categoryId: editCategoryId,
          },
        },
      );
      toastify.success(data?.message || "SubCategory updated successfully.");
      setEditModalOpen(false);
      setEditItem(null);
      setSync((prev) => prev + 1);
    } catch (err) {
      toastify.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" className="py-4 text-[#F8F5F2]">
      <PageName
        title="SubCategory Manager"
        subTitle="Manage nested classifications linked to parent Categories via foreign keys."
      />

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mt-6">
        {/* Left Column: Add SubCategory Form */}
        <div className="lg:col-span-5 xl:col-span-4">
          <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] sticky top-24 text-left">
            <h3 className="text-base font-bold text-[#F8F5F2] mb-1 flex items-center gap-2">
              <GiSparkles className="text-[#C8A165]" /> Create SubCategory
            </h3>
            <p className="text-xs text-[#F8F5F2]/50 mb-6">
              Link a subcategory under a parent category.
            </p>

            <form onSubmit={handleAddSubCategory} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                  Parent Category <span className="text-red-400">*</span>
                </label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all cursor-pointer"
                >
                  <option value="">-- Select Parent Category --</option>
                  {categoriesList?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                  SubCategory Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g. Traditional Makeup, HD Makeup, Airbrush..."
                  value={subCategory}
                  onChange={(e) => setSubCategory(e.target.value)}
                  className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
                />
              </div>

              <div className="pt-2 flex flex-col gap-2.5">
                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-bold text-xs tracking-widest uppercase shadow-lg text-[#0A0A0C] transition-all disabled:opacity-50 cursor-pointer"
                  style={{
                    background:
                      "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                  }}
                >
                  <MdAdd size={16} />
                  Add SubCategory
                </motion.button>

                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="w-full flex items-center justify-center gap-1.5 py-2.5 rounded-2xl border border-[#C8A165]/20 text-[#F8F5F2]/70 hover:text-[#C8A165] hover:bg-white/5 font-medium text-xs transition-colors cursor-pointer"
                >
                  <IoArrowBack size={14} /> Back
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Right Column: SubCategory List Cards */}
        <div className="lg:col-span-7 xl:col-span-8">
          <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] text-left">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b border-[#C8A165]/15">
              <div>
                <h3 className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
                  <BiGitBranch className="text-[#C8A165]" /> All SubCategories
                </h3>
                <p className="text-xs text-[#F8F5F2]/50 mt-0.5">
                  Organized under primary parent categories.
                </p>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <span className="bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 px-3 py-1 rounded-full font-bold text-xs tracking-wider shrink-0">
                  Total: {subCategoriesList?.length || 0}
                </span>
              </div>
            </div>

            {/* Filters & Search Bar */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              <div className="relative flex items-center">
                <MdSearch className="absolute left-3 text-[#C8A165] text-base" />
                <input
                  type="text"
                  placeholder="Search subcategory or category..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-xs bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30"
                />
              </div>

              <div className="flex items-center gap-2">
                <MdFilterList className="text-[#C8A165] text-base shrink-0" />
                <select
                  value={filterCategoryId}
                  onChange={(e) => setFilterCategoryId(e.target.value)}
                  className="w-full text-xs p-2 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165] cursor-pointer"
                >
                  <option value="all">All Parent Categories</option>
                  {categoriesList?.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.category}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* SubCategories Cards Grid */}
            {!subCategoriesList || subCategoriesList.length === 0 ? (
              <div className="text-center py-16 text-[#F8F5F2]/40 text-xs bg-[#16161C] rounded-2xl border border-dashed border-[#C8A165]/20 p-8">
                No subcategories found. Select a parent category and add a new
                subcategory using the form.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {subCategoriesList.map((item, idx) => (
                  <motion.div
                    key={item.id || idx}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.04 }}
                    className="p-4 rounded-2xl border border-[#C8A165]/20 hover:border-[#C8A165]/60 bg-[#16161C] flex items-center justify-between transition-all group shadow-sm"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="w-9 h-9 rounded-xl bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 flex items-center justify-center font-bold text-xs shrink-0">
                        {idx + 1}
                      </div>
                      <div className="truncate">
                        <h4 className="text-sm font-bold text-[#F8F5F2] capitalize group-hover:text-[#C8A165] transition-colors truncate">
                          {item?.subCategory}
                        </h4>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-[0.68rem] bg-[#C8A165]/10 text-[#C8A165] px-2 py-0.5 rounded-md border border-[#C8A165]/20 font-medium truncate">
                            <MdCategory className="inline mr-1 text-[0.6rem]" />
                            {item?.categoryName || "Unknown Category"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 ml-2">
                      <button
                        onClick={() => handleOpenEditModal(item)}
                        className="p-2 text-[#F8F5F2]/40 hover:text-[#C8A165] hover:bg-[#C8A165]/10 rounded-xl transition-colors cursor-pointer"
                        title="Edit SubCategory"
                      >
                        <MdEdit size={16} />
                      </button>

                      <button
                        onClick={() => handleDeleteSubCategory(item)}
                        className="p-2 text-[#F8F5F2]/40 hover:text-red-400 hover:bg-red-500/10 rounded-xl transition-colors cursor-pointer"
                        title="Delete SubCategory"
                      >
                        <MdDelete size={16} />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Edit SubCategory Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: "#121215",
            color: "#F8F5F2",
            borderRadius: "1.5rem",
            border: "1px solid rgba(200, 161, 101, 0.3)",
            maxWidth: "450px",
            width: "100%",
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center border-b border-[#C8A165]/20 pb-3">
          <span className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
            <MdEdit className="text-[#C8A165]" /> Edit SubCategory
          </span>
          <button
            onClick={() => setEditModalOpen(false)}
            className="text-[#F8F5F2]/50 hover:text-[#C8A165] p-1 rounded-lg transition-colors"
          >
            <IoClose size={20} />
          </button>
        </DialogTitle>
        <DialogContent className="pt-4">
          <form onSubmit={handleUpdateSubCategory} className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                Parent Category
              </label>
              <select
                value={editCategoryId}
                onChange={(e) => setEditCategoryId(e.target.value)}
                className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
              >
                {categoriesList?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.category}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                SubCategory Name
              </label>
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
              />
            </div>

            <div className="pt-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 rounded-xl border border-[#C8A165]/20 text-xs font-medium text-[#F8F5F2]/70 hover:bg-white/5 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="px-5 py-2 rounded-xl text-xs font-bold text-[#0A0A0C] tracking-wider uppercase cursor-pointer"
                style={{
                  background:
                    "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                }}
              >
                Save Changes
              </motion.button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {(loading || reduxLoading) && <Loading />}
    </Container>
  );
}
